import React, { useEffect, useState } from "react";
import {
    FiCalendar, FiShield, FiChevronRight, FiSave,
    FiClock, FiCheck, FiAlertCircle, FiEdit3
} from "react-icons/fi";
import axiosInstance from "../utils/api";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const dateSectionConfig = [
    {
        key: "buildUp",
        label: "Build Up",
        description: "Setup and construction phase before the event opens",
        light: "bg-blue-50",
        border: "border-blue-200",
        text: "text-blue-700",
        dot: "bg-blue-500",
        ring: "focus:ring-blue-500",
        badge: "bg-blue-100 text-blue-700",
    },
    {
        key: "showDays",
        label: "Show Days",
        description: "Active event days open to attendees",
        light: "bg-amber-50",
        border: "border-amber-200",
        text: "text-amber-700",
        dot: "bg-amber-400",
        ring: "focus:ring-amber-500",
        badge: "bg-amber-100 text-amber-700",
    },
    {
        key: "teardown",
        label: "Teardown",
        description: "Dismantling and cleanup phase after the event",
        light: "bg-violet-50",
        border: "border-violet-200",
        text: "text-violet-700",
        dot: "bg-violet-500",
        ring: "focus:ring-violet-500",
        badge: "bg-violet-100 text-violet-700",
    },
];



const BadgingSettings = ({ eventId }) => {
    const navigate = useNavigate();
    const [eventData, setEventData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [eventDates, setEventDates] = useState({
        buildUp: { start: "", end: "" },
        showDays: { start: "", end: "" },
        teardown: { start: "", end: "" },
    });

    useEffect(() => {
        const fetchEvent = async () => {
            try {
                setLoading(true);
                const { data } = await axiosInstance.get(`/event/${eventId}`);
                const ev = data.event;
                setEventData(ev);
                setEventDates({
                    buildUp: {
                        start: ev.buildUp?.start?.slice(0, 16) || "",
                        end: ev.buildUp?.end?.slice(0, 16) || "",
                    },
                    showDays: {
                        start: ev.showDays?.start?.slice(0, 16) || "",
                        end: ev.showDays?.end?.slice(0, 16) || "",
                    },
                    teardown: {
                        start: ev.teardown?.start?.slice(0, 16) || "",
                        end: ev.teardown?.end?.slice(0, 16) || "",
                    },
                });
            } catch {
                toast.error("Failed to load event data");
            } finally {
                setLoading(false);
            }
        };
        fetchEvent();
    }, [eventId]);

    const handleSaveDates = async () => {
        try {
            setSaving(true);
            const { data } = await axiosInstance.put(`/event/${eventId}`, {
                buildUp: eventDates.buildUp,
                showDays: eventDates.showDays,
                teardown: eventDates.teardown,
            });
            setEventData(data.data);
            setSaved(true);
            toast.success("Event dates updated successfully");
            setTimeout(() => setSaved(false), 2500);
        } catch {
            toast.error("Failed to update event dates");
        } finally {
            setSaving(false);
        }
    };

    const formatDatePreview = (dateStr) => {
        if (!dateStr) return null;
        return new Date(dateStr).toLocaleString("en-US", {
            month: "short", day: "numeric", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    };

    if (loading) {
        return (
            <div className="space-y-4 animate-pulse">
                <div className="h-8 bg-slate-200 rounded-lg w-48" />
                <div className="h-40 bg-slate-100 rounded-xl" />
                <div className="h-40 bg-slate-100 rounded-xl" />
            </div>
        );
    }

    return (
        <div className="space-y-6 w-full">

            {/* Page Header */}
            <div>
                <h2 className="text-lg font-bold text-slate-800">Badging Settings</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                    Configure event timeline phases and customize badge templates per role
                </p>
            </div>

            {/* ── Badge Customization ── */}
            <section className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2">
                    <FiShield size={14} className="text-indigo-600" />
                    <h3 className="text-sm font-semibold text-slate-800">Badge Customization</h3>
                </div>

                <div className="p-5">
                    <div className="flex items-center justify-between gap-4">
                        <p className="text-xs text-slate-500 leading-relaxed">
                            Customize badges for all roles — Stand Builder, Staff, and Contractor. Control layout, branding, fields, and QR code placement.
                        </p>
                        <button
                            onClick={() => navigate(`/event/${eventId}/badge-customization`)}
                            className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold text-white shadow-sm transition-all hover:shadow-md active:scale-95"
                            style={{ background: "linear-gradient(135deg, #0d2b5c 0%, #1f4e8c 100%)" }}
                            onMouseEnter={e => e.currentTarget.style.background = "linear-gradient(135deg, #0a224a 0%, #183f73 100%)"}
                            onMouseLeave={e => e.currentTarget.style.background = "linear-gradient(135deg, #0d2b5c 0%, #1f4e8c 100%)"}
                        >
                            <FiEdit3 size={12} />
                            Open Designer
                            <FiChevronRight size={12} />
                        </button>
                    </div>
                </div>
            </section>

            {/* ── Event Dates ── */}
            <section className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <FiCalendar size={14} className="text-slate-600" />
                        <h3 className="text-sm font-semibold text-slate-800">Event Dates</h3>
                    </div>
                    <span className="text-[10px] font-medium text-slate-400 flex items-center gap-1">
                        <FiClock size={10} /> Timeline phases
                    </span>
                </div>

                <div className="p-5 space-y-3 bg-slate-50">
                    {dateSectionConfig.map(({ key, label, description, light, border, text, dot, ring, badge }) => (
                        <div key={key} className={`rounded-xl border ${border} ${light} overflow-hidden`}>

                            <div className={`px-4 py-2.5 border-b ${border} flex items-center justify-between`}>
                                <div className="flex items-center gap-2">
                                    <span className={`w-2 h-2 rounded-full ${dot} flex-shrink-0`} />
                                    <span className={`text-[11px] font-bold uppercase tracking-wide ${text}`}>{label}</span>
                                </div>
                                {eventDates[key].start && eventDates[key].end && (
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${badge}`}>
                                        Configured
                                    </span>
                                )}
                            </div>

                            <div className="p-4 space-y-3">
                                <p className="text-[11px] text-slate-500">{description}</p>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Start</label>
                                        <input
                                            type="datetime-local"
                                            value={eventDates[key].start}
                                            onChange={(e) =>
                                                setEventDates(prev => ({
                                                    ...prev,
                                                    [key]: { ...prev[key], start: e.target.value },
                                                }))
                                            }
                                            className={`w-full border border-slate-200 bg-white rounded-lg px-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-2 ${ring} focus:border-transparent shadow-sm transition`}
                                        />
                                        {eventDates[key].start && (
                                            <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                                                <FiClock size={9} /> {formatDatePreview(eventDates[key].start)}
                                            </p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">End</label>
                                        <input
                                            type="datetime-local"
                                            value={eventDates[key].end}
                                            onChange={(e) =>
                                                setEventDates(prev => ({
                                                    ...prev,
                                                    [key]: { ...prev[key], end: e.target.value },
                                                }))
                                            }
                                            className={`w-full border border-slate-200 bg-white rounded-lg px-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-2 ${ring} focus:border-transparent shadow-sm transition`}
                                        />
                                        {eventDates[key].end && (
                                            <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                                                <FiClock size={9} /> {formatDatePreview(eventDates[key].end)}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {eventDates[key].start && eventDates[key].end && (() => {
                                    const diff = new Date(eventDates[key].end) - new Date(eventDates[key].start);
                                    if (diff <= 0) return (
                                        <div className="flex items-center gap-1.5 text-[10px] text-red-500 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5">
                                            <FiAlertCircle size={10} /> End time must be after start time
                                        </div>
                                    );
                                    const hours = Math.floor(diff / 3600000);
                                    const days = Math.floor(hours / 24);
                                    const remHours = hours % 24;
                                    return (
                                        <div className={`flex items-center gap-1.5 text-[10px] font-medium ${text} rounded-lg px-2.5 py-1.5 border ${border}`}>
                                            <FiCheck size={10} />
                                            Duration: {days > 0 ? `${days}d ` : ""}{remHours > 0 ? `${remHours}h` : ""}
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Save footer */}
                <div className="px-5 py-3.5 bg-white border-t border-slate-100 flex items-center justify-between gap-3">
                    <p className="text-[11px] text-slate-400">Changes are saved globally for all participants in this event.</p>
                    <button
                        onClick={handleSaveDates}
                        disabled={saving}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold text-white transition-all shadow-sm active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed ${saved ? "bg-emerald-500" : ""}`}
                        style={!saved ? { background: "linear-gradient(135deg, #0f2042 0%, #1a3a6e 100%)" } : {}}
                        onMouseEnter={e => { if (!saved) e.currentTarget.style.background = "linear-gradient(135deg, #0a1a35 0%, #163060 100%)"; }}
                        onMouseLeave={e => { if (!saved) e.currentTarget.style.background = "linear-gradient(135deg, #0f2042 0%, #1a3a6e 100%)"; }}
                    >
                        {saving ? (
                            <>
                                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Saving…
                            </>
                        ) : saved ? (
                            <><FiCheck size={12} /> Saved!</>
                        ) : (
                            <><FiSave size={12} /> Save Changes</>
                        )}
                    </button>
                </div>
            </section>
        </div>
    );
};

export default BadgingSettings;